// THE viewer toolbar. Not a lookalike of the model view's - the same one.
//
// Owner, 2026-09-23: "can we use same icons used in model view and same
// location for all views, literally copy the same exact code". He was
// reacting to two different looks for the same commands: the model view
// drew MDBIcon buttons bottom-left, while every other 3D page got a strip
// of TEXT labels. Same commands, same viewer, two appearances, and the
// model view drifting on its own because it was the special case.
//
// So this is that markup, lifted verbatim - same icons, same classes,
// same MDBBtnGroup position - and the model view now renders THIS rather
// than its own copy. There is no longer a page that owns the toolbar.
//
// === THE IDS ARE LOAD-BEARING, AND EXACTLY ONE OF EACH MAY EXIST ===
//
// viewer/index.js binds G, M, C, I on window and reaches the buttons by
// document.getElementById(...); toggleMeasurement and toggleClipping look
// their own button up by id rather than taking the element they were
// given. So the ids must survive the move, and a page must NOT keep its
// own copy beside this one - getElementById returns the FIRST match, so a
// duplicate means the key lights one button while the click lights the
// other, and the two disagree about whether the tool is on. That is the
// failure this component exists to make impossible.
import React, { useCallback } from "react";
import { MDBBtn, MDBBtnGroup, MDBIcon } from "mdbreact";
import resolveViewer from "./resolveViewer";
import ViewerEffectsButton from "./ViewerEffectsButton";

// key: what the page asks for / leaves out
// id:  the element id the keyboard path depends on, where there is one
// method: the viewer call. `probe` is the same method - each proves the
//         viewer is the viewer, as resolveViewer expects.

// ONE ROW, SMALLER, ALL UNIFORM (owner, 2026-09-24): "keep all the buttons
// aligned at the bottom view, 1 row and make them slightly smaller and all
// uniform".
//
// THIS HAS TO BE A STYLESHEET RULE AND IT HAS TO CARRY !important, which is
// not a preference. The existing rule is
//     #WebglViewer .modal3d-tool-btn { padding: 10px !important; margin-right: 13px !important }
// and it lives in the two TimelineViewer stylesheets only - so today the
// buttons are one size on the model view and the asset viewer and another
// size everywhere else, which is half of what he means by uniform. An
// inline style cannot beat !important, so matching it is the only way to
// win on those two pages and set the size on the other seven.
//
// Injected once by the component rather than added to a page stylesheet,
// for the reason .viewer-zoom taught us: a rule that lives in two of nine
// stylesheets is inert on the other seven.
const STYLE_ID = 'bl-viewer-toolbar-style';
const UNIFORM_CSS = `
.bl-viewer-toolbar { display: inline-flex !important; flex-wrap: nowrap !important; align-items: center;
  gap: 4px; max-width: 100%; overflow-x: auto; overflow-y: hidden; pointer-events: auto; }
.bl-viewer-toolbar::-webkit-scrollbar { height: 4px; }
.bl-viewer-toolbar .modal3d-tool-btn,
.bl-viewer-toolbar .btn {
  width: 30px !important; height: 30px !important; min-width: 30px !important;
  padding: 0 !important; margin: 0 !important; display: inline-flex !important;
  align-items: center !important; justify-content: center !important;
  border-radius: 4px !important; flex: 0 0 auto !important; line-height: 1 !important;
}
.bl-viewer-toolbar .modal3d-tool-btn .fa, .bl-viewer-toolbar .modal3d-tool-btn svg { font-size: 13px !important; }
`;
function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = UNIFORM_CSS;
  document.head.appendChild(el);
}

export const TOOLS = [
  { key: "reload", icon: "refresh", title: "Reload the viewer" },
  { key: "zoomIn", icon: "plus", title: "Zoom in", method: "onZoom", arg: 0.1 },
  { key: "zoomOut", icon: "minus", title: "Zoom out", method: "onZoom", arg: -0.1 },
  { key: "screenshot", icon: "camera", hint: "S", title: "Take a screenshot of the current view", method: "onScreenShot" },
  { key: "home", icon: "home", hint: "H", title: "Go to Home view", method: "onInitialPosition" },
  { key: "target", icon: "map-marker", hint: "F", title: "Zoom to selected item in viewer", method: "targetItems" },
  { key: "grid", icon: "braille", hint: "G", id: "gridToggleBtn", title: "Toggle Grid in viewer", method: "onShowGrid", pass: true },
  { key: "measure", icon: "tape", hint: "M", id: "measurementToggleBtn", title: "Toggle Measurement Tool in viewer", method: "toggleMeasurement", pass: true },
  { key: "section", icon: "cut", hint: "C", id: "clippingToggleBtn", title: "Toggle Clipping Tool in viewer", method: "toggleClipping", pass: true },
  { key: "isolate", icon: "box", hint: "I", id: "isolateToggleBtn", title: "Toggle Isolation Tool in viewer", method: "toggleIsolate", pass: true },
  // Hide and Unhide all joined on 2026-09-22 and had no icons, only text.
  // eye-slash / eye is the pair a person reads without being told, and it
  // sits in the same family as the rest. They are ACTIONS, not toggles,
  // so unlike grid and isolate they light nothing - there is no state for
  // a button to reflect - and they carry no key hint because they have no
  // shortcut yet: H is Home, and the owner picks the key.
  { key: "hide", icon: "eye-slash", title: "Hide selected", method: "hideSelected" },
  { key: "unhide", icon: "eye", title: "Unhide everything hidden", method: "unhideAll" },
];

const DEFAULT = TOOLS.map((t) => t.key);

export default function ViewerToolbar({
  viewer,
  tools = DEFAULT,
  gear = true,
  // The group's own positioning class. Every page uses the model view's
  // `viewer-zoom` - that is the "same location" half of the owner's ask.
  // SocketViewer is the one exception and it is deliberate: it is the
  // desktop-embedded viewer, it positions its toolbar with its own
  // class, and nobody here can see that window to judge a move. It takes
  // the ICONS and keeps its PLACE until someone who can see it says
  // otherwise.
  groupClassName = "viewer-zoom",
  className = "",
  children = null,
}) {
  ensureStyle();
  const wanted = Array.isArray(tools) ? TOOLS.filter((t) => tools.indexOf(t.key) !== -1) : TOOLS;

  const run = useCallback((tool, el) => {
    if (tool.key === "reload") { window.location.reload(false); return; }
    const v = resolveViewer(viewer, tool.method);
    if (!v || typeof v[tool.method] !== "function") return;
    try {
      // `pass` marks the four that toggle 'buttonEnabled' on the element
      // they are HANDED. Calling them with no argument flips the viewer's
      // state while nothing lights, which is how a control and its key
      // end up disagreeing.
      if (tool.pass) v[tool.method](el);
      else if (tool.arg !== undefined) v[tool.method](tool.arg);
      else v[tool.method]();
    } catch (e) { /* the model still draws */ }
  }, [viewer]);

  return (
    <MDBBtnGroup
      size="sm"
      className={`position-absolute bl-viewer-toolbar ${groupClassName} ${className}`}
      // IT MUST FIT THE PANE IT IS IN. Twelve buttons at 49px is 705px,
      // and the tracking pane is 630 - so the group overflowed, and
      // because it is absolute against the right it overflowed LEFT, off
      // the window edge (Mark, measured: box x=-11 after his wrapper was
      // widened, x=-327 before). No mount can fix a group wider than its
      // container; the group has to wrap.
      // maxWidth keeps it inside whatever it is given, flexWrap lets it
      // use a second row when one is not enough. On a wide pane - the
      // model view, the timeline - nothing changes, because it fits.
      style={{
        // ONE ROW. flexWrap was "wrap" between 2026-09-23 and 24: my answer
        // to 12 buttons at 49px not fitting a 630px pane. The owner has
        // asked for one row instead, so the buttons are 30px and uniform
        // and the row is 13 x 34 = about 442px, which fits. If a container
        // is ever narrower than that the row SCROLLS horizontally rather
        // than wrapping or running off the edge - said out loud because he
        // asked to be told rather than have it wrap silently.
        maxWidth: "100%", flexWrap: "nowrap", justifyContent: "flex-end",
        // pointerEvents MUST be re-enabled here. The wrappers that give
        // this group its width now span the whole pane, and they set
        // pointerEvents:none so a full-width invisible box does not
        // swallow every click on the model behind it. Without this line
        // the buttons would render perfectly and do nothing - which is
        // the failure that looks like working.
        pointerEvents: "auto",
      }}
    >
      <p id="clippingTooltip" />
      {wanted.map((t) => (
        <MDBBtn
          key={t.key}
          id={t.id || undefined}
          title={t.hint ? `${t.title} (${t.hint})` : t.title}
          size="sm"
          color=""
          className="bg-white modal3d-tool-btn"
          onClick={(e) => run(t, e.currentTarget)}
        >
          {/* ICON ONLY. The inline "[G]" / "[M]" hints made those buttons
              wider than the ones without them - the other half of what the
              owner means by uniform - so the shortcut moved into the
              tooltip, where it is still discoverable and costs no width. */}
          <MDBIcon icon={t.icon} className="font-size-1" />
        </MDBBtn>
      ))}
      {gear ? <ViewerEffectsButton viewer={viewer} variant="mdb" /> : null}
      {children}
    </MDBBtnGroup>
  );
}
